import { SERVER_URL } from '@common/constants';
import { API_ENDPOINTS } from '@common/constants/apiEndpoint';
import axiosInstance from '@common/services/axiosIntance/axiosService';
import { get } from 'lodash';
import moment from 'moment';
import queryString from 'query-string';
import { useCallback, useState } from 'react';
import { Platform } from 'react-native';
import { getValueBinding } from '../shared';
import {
  IInvoicePDF,
  InvoicePdfBindingValue,
  PdfContent,
  PdfItem,
} from './type';

const getLabelFromCanvas = (value: any): string => {
  if (typeof value === 'string' || typeof value === 'number') {
    return value.toString();
  }
  if (Array.isArray(value)) {
    return value.map(getLabelFromCanvas).join('');
  }
  if (typeof value === 'object') {
    return value['label'] || value['parsedFormula'] || '';
  }
  return '';
};
const getBindingValueFromCanvas = (
  props: IInvoicePDF
): Omit<PdfContent, 'orderLines'> => {
  return props.attributes as Omit<PdfContent, 'orderLines'>;
};

const transformCustomFields = (
  value: InvoicePdfBindingValue,
  props: IInvoicePDF
): Omit<PdfContent, 'orderLines'> => {
  if (!value) return value;
  const logo =
    get(value, 'invoiceInformation.logo', '') ||
    getLabelFromCanvas(
      get(props, 'attributes.invoiceInformation.imageUrl', '')
    );
  return {
    currency: 'JPY',
    orderHeaders: {
      itemName: get(value, 'orderHeaders.itemName', ''),
      itemQuantity: get(value, 'orderHeaders.itemQuantity', ''),
      itemPrice: get(value, 'orderHeaders.itemPrice', ''),
      total: get(value, 'orderHeaders.total', ''),
    },
    tax: {
      enabled: get(props, 'attributes.tax.enabled', false),
      title: get(value, 'tax.title', ''),
      value: Number(get(value, 'tax.value', 10)),
      type: get(value, 'tax.type', 'percentage'),
    },
    shippingFee: {
      enabled: get(props, 'attributes.shippingFee.enabled', false),
      title: get(value, 'shippingFee.title', ''),
      value: Number(get(value, 'shippingFee.value', 0)),
      type: get(value, 'shippingFee.type', 'amount'),
    },
    senderInformation: {
      title: get(value, 'senderInformation.title', ''),
      name: get(value, 'senderInformation.name', ''),
      description: get(value, 'senderInformation.description', ''),
    },
    receiverInformation: {
      title: get(value, 'receiverInformation.title', ''),
      name: get(value, 'receiverInformation.name', ''),
      description: get(value, 'receiverInformation.description', ''),
    },
    invoiceInformation: {
      logo,
      title: get(value, 'invoiceInformation.title', ''),
      invoiceID: get(value, 'invoiceInformation.invoiceID', ''),
      notes: get(value, 'invoiceInformation.notes', ''),
    },
    customFields1: {
      enabled: get(props, 'attributes.customFields1.enabled', false),
      title: get(value, 'customFields1.title', ''),
      value: get(value, 'customFields1.value', ''),
    },
    customFields2: {
      enabled: get(props, 'attributes.customFields2.enabled', false),
      title: get(value, 'customFields2.title', ''),
      value: get(value, 'customFields2.value', ''),
    },
    customFields3: {
      enabled: get(props, 'attributes.customFields3.enabled', false),
      title: get(value, 'customFields3.title', ''),
      value: get(value, 'customFields3.value', ''),
    },
    customFields4: {
      enabled: get(props, 'attributes.customFields4.enabled', false),
      title: get(value, 'customFields4.title', ''),
      value: get(value, 'customFields4.value', ''),
    },
  };
};

export const useInvoicePdf = (props: IInvoicePDF) => {
  const {
    record,
    data,
    isInCustomList,
    id,
    initId,
    dependencies,
    dataBinding,
  } = props;
  const [shortUrlCreating, setShortUrlCreating] = useState(false);
  const [invoicePdfUrl, setInvoicePdfUrl] = useState('');
  const isCanvas =
    Platform.OS === 'web' &&
    !queryString.parse(window?.location?.search)?.target;

  const _bindingValue = getValueBinding(
    isInCustomList ? initId : id,
    isInCustomList ? record : dataBinding?.[0] || {},
    props
  ) as any;
  const bindingValue = isCanvas
    ? getBindingValueFromCanvas(props)
    : transformCustomFields(_bindingValue, props);

  const orderLines: PdfItem[] = isCanvas
    ? Array.isArray(props.attributes.items)
      ? [props.attributes.items[0].orderLines]
      : []
    : dataBinding?.map((item) => ({
        itemDescription: get(item, 'orderLines.itemDescription', ''),
        itemName: get(item, 'orderLines.itemName', ''),
        itemPrice: Number(get(item, 'orderLines.itemPrice', 0)),
        itemQuantity: Number(get(item, 'orderLines.itemQuantity', 0)),
      })) || [];
  const pdfContent: PdfContent = {
    ...bindingValue,
    orderLines,
  };

  const getInvoicePdfUrl = useCallback(async () => {
    const shortUrlKey = isCanvas
      ? `invoice-pdf-canvas-${id}`
      : `invoice-pdf-${id}`;
    setShortUrlCreating(true);
    const oldShortUrl: { id: string }[] = await axiosInstance({
      method: 'GET',
      url: API_ENDPOINTS.SHORT_URL,
      params: {
        shortUrl: shortUrlKey,
        appId: dependencies.appInfor.appId,
        $limit: 1,
      },
    }).then((r) => {
      const data = r.data as any;
      if (data.data && Array.isArray(data.data)) {
        return data.data;
      }
      return data;
    });
    if (!oldShortUrl.length) {
      await axiosInstance({
        method: 'POST',
        url: API_ENDPOINTS.SHORT_URL,
        data: {
          shortUrl: shortUrlKey,
          url: JSON.stringify(pdfContent),
          expiredAt: moment().add(1, 'day'),
          appId: dependencies.appInfor.appId,
        },
        params: {
          appId: dependencies.appInfor.appId,
        },
      });
    } else {
      await axiosInstance({
        method: 'PATCH',
        url: `${API_ENDPOINTS.SHORT_URL}/${oldShortUrl[0].id}`,
        data: {
          shortUrl: shortUrlKey,
          url: JSON.stringify(pdfContent),
          expiredAt: moment().add(1, 'day'),
          appId: dependencies.appInfor.appId,
        },
        params: {
          appId: dependencies.appInfor.appId,
        },
      });
    }
    setShortUrlCreating(false);
    const qr = queryString.stringify({
      shortUrl: shortUrlKey,
      t: moment().format('YYMMDDhhmm'),
    });
    setInvoicePdfUrl(`${SERVER_URL}/invoice-pdf-element?${qr}`);
  }, [dataBinding, record]);

  return {
    bindingValue,
    isCanvas,
    orderLines,
    getInvoicePdfUrl,
    pdfContent,
    shortUrlCreating,
    invoicePdfUrl,
  };
};
